import { createClient } from '@supabase/supabase-js';

// These should be set as environment variables in production
// For now, you'll need to add these to your .env file or replace with actual values
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

// Validate that we have required config
if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase configuration. Please set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Helper functions for authentication
export const signInWithGoogle = async () => {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: `${window.location.origin}`,
    },
  });
  return { data, error };
};

export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  return { error };
};

export const getCurrentUser = async () => {
  const { data: { user }, error } = await supabase.auth.getUser();
  return { user, error };
};

export const getCurrentSession = async () => {
  const { data: { session }, error } = await supabase.auth.getSession();
  return { session, error };
};

// Test function to verify Supabase connection
// Note: We skip getSession() because it hangs in some configurations
export const testSupabaseConnection = async () => {
  console.log('Testing Supabase connection...');
  console.log('Supabase URL:', process.env.REACT_APP_SUPABASE_URL);
  console.log('Supabase client:', supabase);
  
  try {
    // Test 1: Simple database query (if profiles table exists)
    console.log('Test 1: Testing database query...');
    let queryWorks = false;
    try {
      const { data: queryData, error: queryError } = await supabase
        .from('profiles')
        .select('id')
        .limit(1);
      console.log('Query result:', { queryData, queryError });
      queryWorks = !queryError || queryError.code !== 'PGRST116'; // PGRST116 = table not found, which is ok
    } catch (queryErr) {
      console.log('Query test failed (table might not exist):', queryErr);
    }
    
    // Test 2: Test RPC mechanism (even if function doesn't exist, we should get an error, not hang)
    console.log('Test 2: Testing RPC mechanism with non-existent function...');
    let rpcWorks = false;
    let rpcError = null;
    try {
      // Add timeout to RPC test
      const rpcPromise = supabase.rpc('test_function_that_does_not_exist');
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('RPC test timed out after 5 seconds')), 5000)
      );
      
      const result = await Promise.race([rpcPromise, timeoutPromise]) as any;
      console.log('RPC test result (function should not exist):', result);
      rpcWorks = true; // If we got here, RPC mechanism works
      rpcError = result?.error;
    } catch (rpcErr: any) {
      if (rpcErr?.message?.includes('timed out')) {
        console.error('RPC test TIMED OUT - RPC mechanism is not working');
        return { connected: false, error: new Error('RPC calls are hanging/timing out') };
      }
      console.log('RPC test exception (expected for non-existent function):', rpcErr);
      rpcWorks = true; // Getting an error means the RPC mechanism works, function just doesn't exist
    }
    
    return { 
      connected: rpcWorks, // RPC mechanism working is the key test
      error: rpcError,
      queryWorks,
      rpcWorks,
    };
  } catch (err) {
    console.error('Connection test failed:', err);
    return { connected: false, error: err };
  }
};

